import { AnimatePresence, motion } from 'framer-motion';

import type { FormProps, SanityDefaultForm } from '#/lib/sanity/queries/form';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { Button } from '#/components/Button';
import { Form } from '#/components/Form/Single';

import { addBusinessEmailValidation } from '../utils';
import { FormConfirmation } from './Confirmation';
import { FormErrorMessage } from './ErrorMessage';
import { FormFields } from './FormFields';

export function FormCustomSingle(props: FormProps<SanityDefaultForm>) {
  const formFieldsSchema = addBusinessEmailValidation(props.form);

  return (
    <div className={cn('relative', props.className)}>
      <Form
        formTitle={props.form.formTitle}
        hubspotCampaignId={props.form.hubspotCampaignId}
        hubspotConvertingFormType={props.form.hubspotConvertingFormType}
        contentPieceTitle={props.form.contentPieceTitle}
        hubspotEmailSubscriptionIds={props.form.hubspotEmailSubscriptionIds}
        chiliPiperRouterId={props.form.chiliPiperRouterId}
        formFieldsSchema={formFieldsSchema}
        trackingEventName={props.form.trackingEventName}
        trackingEventProperties={props.form.trackingEventProperties}
        onSubmitSuccess={props.onSubmitSuccess}
        onSubmitError={props.onSubmitError}
        ConfirmationComponent={() => (
          <FormConfirmation
            confirmation={props.confirmation}
            theme={props.buttonTheme ?? props.theme}
          />
        )}
      >
        {({ control, formState }) => {
          return (
            <div className="grid grid-cols-2 gap-20">
              <FormFields
                fields={props.form.fields}
                control={control}
                theme={props.theme}
              />
              <FormErrorMessage
                formState={formState}
                className="col-span-full"
              />
              <Button
                className="col-span-full"
                variant="form"
                formTheme={props.theme}
                type="submit"
              >
                <AnimatePresence mode="popLayout">
                  {formState.isSubmitting ? (
                    <motion.div
                      key="spinner"
                      className="mx-auto size-14 animate-spin rounded-full border-2 border-r-transparent"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <p className="sr-only">Submitting form…</p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {props.form.submitButtonLabel}
                    </motion.div>
                  )}
                </AnimatePresence>
              </Button>
              {props.form.legalText && props.form.legalText.length > 0 ? (
                <BasicText
                  className={cn(
                    'legal-text col-span-full mt-10 font-light text-grayscale-05',
                    props.theme === 'dark' && 'text-cloud',
                  )}
                  value={props.form.legalText}
                />
              ) : null}
            </div>
          );
        }}
      </Form>
    </div>
  );
}
